import React from "react";

import Framework from "@beardeddevops/react.framework";
import {Parts, Types} from "components";
import API from "@beardeddevops/react.api";
import GoogleMapReact from "google-map-react";
import moment from "moment";
import Select from "react-select";
import {Button, CustomInput} from "reactstrap";
import {Link} from "react-router-dom";
import {parseLatLngString, resolveLatLng} from "../../constants/Utilities";
import {getGoogleMapBootstrapURLKeys} from "../../constants/GoogleMaps";
import {driveTimeForLeg, legMidpoint, offsetLegLabelPosition, driveTimeLabelPosition} from "../../constants/DriveTime";

const USER_COLORS = [
	"#FF0000", "#673ab7", "#ff9800", "#025E05",
	"#03a9f4", "#A00000", "#4caf50", "#8bc34a", "#cddc39",
	"#ff5722", "#9c27b0", "#795548", "#3f51b5", "#607d8b",
	"#000000"
];

const buildHouseSymbol = (color) => ({
	path: "M -3.5437 0 V 4.3312 H -1.1813 V 1.575 H 1.1813 V 4.3312 H 3.5437 V 0 L 0 -2.7563 Z",
	strokeColor: "#000000",
	fillColor: color,
	fillOpacity: 1,
});

const buildHoverTooltipOverlayClass = (maps) => class extends maps.OverlayView {
	constructor(position, html) {
		super();
		this.position = new maps.LatLng(position.lat, position.lng);
		this.html = html;
	}

	onAdd() {
		this.div = document.createElement('div');
		this.div.style.position = 'absolute';
		this.div.style.pointerEvents = 'none';
		this.div.style.zIndex = '1000';
		this.div.innerHTML = this.html;
		this.getPanes().floatPane.appendChild(this.div);
	}

	draw() {
		if (!this.div) {
			return;
		}
		const projection = this.getProjection();
		if (!projection) {
			return;
		}
		const point = projection.fromLatLngToDivPixel(this.position);
		if (point) {
			this.div.style.left = `${point.x}px`;
			this.div.style.top = `${point.y}px`;
			this.div.style.transform = 'translate(-50%, -110%)';
		}
	}

	onRemove() {
		if (this.div?.parentNode) {
			this.div.parentNode.removeChild(this.div);
		}
		this.div = null;
	}
};

export default class DispatchMap extends Framework.Components.Collection {
	constructor(props) {
		super(props, Types.Users._name);
		this.request = 'GET_FIELD_ENGINEERS_MAPS';

		this.state.name = this.props.name || '';

		this.state.filterRegion = null;
		this.state.center = null;
		this.state.defaultCenter = {
			lat: 33.44838,
			lng: -112.07404
		};
		this.state.regions = [];
		this.state.users = [];
		this.state.selectedUsers = [];
		this.state.selectedRegion = null;
		this.state.routes = [];
		this.state.hoveredLeg = null;
		this.state.showDriveTimes = true;

		this.mapRef = React.createRef();
		this.polylineRefs = [];
		this.mapMarkerRefs = [];
		this.customerMarker = null;
		this.hoverTooltipOverlay = null;
		this.hoverTooltipOverlayClass = null;
		this.centeredForKey = null;
	}

	componentWillUnmount() {
		this.clearPolylines();
		this.clearMapMarkers();
		this.clearCustomerMarker();
		this.closeHoverTooltip();
		if (this.dataInterval) {
			clearInterval(this.dataInterval);
		}
		if (this.fetchTimeout) {
			clearTimeout(this.fetchTimeout);
		}
	}

	async componentDidMount() {
		this._ismounted = true;
		await this.getRegions();
		if (!this.dataInterval && !this.props.noAutoRefresh) {
			this.dataInterval = setInterval(async () => {
				if (this.props.filterRegion && !this.props.noRefresh) {
					await this.getFEMaps();
				}
			}, 30000)
		}
		let selectedRegion = this.state.regions.filter(region => {
			return region.pkey == (this.state.filterRegion ?? this.props.filterRegion)
		})[0];

		if (selectedRegion) {
			this.setState({selectedRegion: {value: selectedRegion.pkey, label: selectedRegion.name}})
		}
		if (this.props.filterRegion) {
			await this.getFEMaps(true);
		}
	}

	async componentDidUpdate(prevProps, prevState) {
		if ((this.props.filterRegion || this.state.filterRegion) && (this.props.filterRegion !== this.state.filterRegion || prevProps.filterDate !== this.props.filterDate)) {
			if (prevProps.filterRegion !== this.props.filterRegion) {
				this.state.filterRegion = this.props.filterRegion;
			}
			if (this.fetchTimeout) {
				clearTimeout(this.fetchTimeout);
			}

			this.fetchTimeout = setTimeout(async () => {
				await this.getFEMaps(true);
			}, 300);
		}
		if (this.props.selectedAddress !== prevProps.selectedAddress) {
			this.updateCustomerMarker();
			this.panToCustomer();
		}
		if (
			prevState.routes !== this.state.routes ||
			prevState.selectedUsers !== this.state.selectedUsers ||
			prevState.showDriveTimes !== this.state.showDriveTimes
		) {
			this.syncPolylines();
		} else if (prevState.hoveredLeg !== this.state.hoveredLeg) {
			this.updatePolylineStyles();
			this.updateMapMarkerStyles();
			this.updateHoverTooltip();
		}
	}

	apiIsLoaded = (map, maps) => {
		this.state.map = map;
		this.state.maps = maps;
		this.syncPolylines();
		this.updateCustomerMarker();
	}

	clearCustomerMarker = () => {
		if (this.customerMarker) {
			if (this.state.maps) {
				this.state.maps.event.clearInstanceListeners(this.customerMarker);
			}
			this.customerMarker.setMap(null);
			this.customerMarker = null;
		}
	}

	getCustomerCoords = () => {
		const address = this.props.selectedAddress;
		if (!address) {
			return null;
		}
		const lat = parseFloat(address.lat);
		const lng = parseFloat(address.lng);
		if (Number.isNaN(lat) || Number.isNaN(lng)) {
			return null;
		}
		return {lat, lng};
	}

	updateCustomerMarker = () => {
		this.clearCustomerMarker();
		const coords = this.getCustomerCoords();
		if (!coords || !this.state.map || !this.state.maps) {
			return;
		}

		this.customerMarker = new this.state.maps.Marker({
			position: coords,
			map: this.state.map,
			zIndex: 200,
		});
	}

	panToCustomer = () => {
		const coords = this.getCustomerCoords();
		if (!coords || !this.state.map || !this.state.maps) {
			return;
		}
		this.state.map.panTo(new this.state.maps.LatLng(coords.lat, coords.lng));
	}

	panToRegion = (regionCoords) => {
		if (!this.state.map || !this.state.maps || !regionCoords) {
			return;
		}
		this.state.map.panTo(new this.state.maps.LatLng(
			parseFloat(regionCoords[0]),
			parseFloat(regionCoords[1])
		));
	}

	clearPolylines = () => {
		this.polylineRefs.forEach(({polyline}) => {
			if (this.state.maps) {
				this.state.maps.event.clearInstanceListeners(polyline);
			}
			polyline.setMap(null);
		});
		this.polylineRefs = [];
	}

	clearMapMarkers = () => {
		this.mapMarkerRefs.forEach(({marker}) => {
			if (this.state.maps) {
				this.state.maps.event.clearInstanceListeners(marker);
			}
			marker.setMap(null);
		});
		this.mapMarkerRefs = [];
	}

	closeHoverTooltip = () => {
		if (this.hoverTooltipOverlay) {
			this.hoverTooltipOverlay.setMap(null);
			this.hoverTooltipOverlay = null;
		}
	}

	getVisibleRoutes = () => {
		const {routes, selectedUsers} = this.state;
		if (!selectedUsers || selectedUsers.length === 0) {
			return routes;
		}
		const selected = new Set(selectedUsers.map((u) => u.value.toString()));
		return routes.filter((route) => selected.has(route.fePkey.toString()));
	}

	isRouteDimmed = (fePkey) => {
		const hovered = this.state.hoveredLeg;
		if (!hovered) {
			return false;
		}
		return hovered.fePkey.toString() !== fePkey.toString();
	}

	setHoveredLeg = (hoveredLeg) => {
		this.setState({hoveredLeg});
	}

	shouldRecenterMap = () => {
		if (!this.props.noAutoRefresh) {
			return true;
		}
		const key = `${this.state.filterRegion ?? this.props.filterRegion}|${this.props.filterDate}`;
		if (key !== this.centeredForKey) {
			this.centeredForKey = key;
			return true;
		}
		return false;
	}

	syncPolylines = () => {
		if (!this.state.map || !this.state.routes.length) {
			return;
		}

		this.clearPolylines();
		this.clearMapMarkers();
		this.closeHoverTooltip();
		const visibleRoutes = this.getVisibleRoutes();

		visibleRoutes.forEach((route) => {
			const houseSymbol = buildHouseSymbol(route.color);

			route.legs.forEach((leg) => {
				const polyline = new this.state.maps.Polyline({
					path: leg.path,
					map: this.state.map,
					strokeColor: route.color,
					strokeOpacity: 1,
					strokeWeight: 4,
					icons: leg.isDummy ? [{
						icon: houseSymbol,
						offset: '0%'
					}] : [],
				});

				polyline.addListener('mouseover', () => {
					this.setHoveredLeg({fePkey: route.fePkey, legIndex: leg.index});
				});
				polyline.addListener('mouseout', () => {
					this.setHoveredLeg(null);
				});
				polyline.addListener('click', () => {
					if (this.props.noRefresh) return;
					this.setState({selectedUsers: [{value: route.fePkey, label: route.name}]});
				});

				this.polylineRefs.push({
					polyline,
					fePkey: route.fePkey,
					legIndex: leg.index,
					color: route.color,
				});

				if (!leg.isDummy && leg.stopNumber != null) {
					const stopMarker = new this.state.maps.Marker({
						position: {lat: leg.stopLat, lng: leg.stopLng},
						map: this.state.map,
						icon: {
							path: this.state.maps.SymbolPath.CIRCLE,
							fillColor: route.color,
							fillOpacity: 1,
							strokeColor: '#000000',
							strokeWeight: 2,
							scale: 10,
						},
						label: {
							text: String(leg.stopNumber),
							color: '#ffffff',
							fontSize: '11px',
							fontWeight: 'bold',
						},
						zIndex: 150,
					});
					this.mapMarkerRefs.push({marker: stopMarker, fePkey: route.fePkey});
				}

				if (this.state.showDriveTimes && !leg.isDummy && leg.drive_time > 0) {
					const labelPosition = driveTimeLabelPosition(leg.path[0], leg.path[1]);
					const driveMarker = new this.state.maps.Marker({
						position: labelPosition,
						map: this.state.map,
						icon: {
							path: 'M -50,-16 L 50,-16 L 50,16 L -50,16 Z',
							fillColor: '#ffffff',
							fillOpacity: 1,
							strokeColor: '#333333',
							strokeWeight: 1.5,
							scale: 0.6,
						},
						label: {
							text: `${leg.drive_time} min`,
							color: '#000000',
							fontSize: '13px',
							fontWeight: 'bold',
						},
						zIndex: 250,
					});
					this.mapMarkerRefs.push({marker: driveMarker, fePkey: route.fePkey});
				}
			});

			if (this.props.noRefresh) {
				const nameMarker = new this.state.maps.Marker({
					position: {lat: route.lastLat, lng: route.lastLng},
					map: this.state.map,
					icon: {
						path: this.state.maps.SymbolPath.CIRCLE,
						scale: 0,
					},
					label: {
						text: route.name,
						color: route.color,
						fontSize: '14px',
						fontWeight: 'bold',
					},
					zIndex: 50,
				});
				this.mapMarkerRefs.push({marker: nameMarker, fePkey: route.fePkey});
			}
		});

		this.updatePolylineStyles();
		this.updateMapMarkerStyles();
	}

	updatePolylineStyles = () => {
		const hovered = this.state.hoveredLeg;

		this.polylineRefs.forEach(({polyline, fePkey, legIndex, color}) => {
			const dimmed = this.isRouteDimmed(fePkey);
			const isHovered = hovered
				&& hovered.fePkey.toString() === fePkey.toString()
				&& hovered.legIndex === legIndex;

			polyline.setOptions({
				strokeColor: color,
				strokeOpacity: dimmed ? 0.22 : 1,
				strokeWeight: isHovered ? 6 : 4,
			});
		});
	}

	updateMapMarkerStyles = () => {
		this.mapMarkerRefs.forEach(({marker, fePkey}) => {
			const dimmed = this.isRouteDimmed(fePkey);
			marker.setOpacity(dimmed ? 0.22 : 1);
		});
	}

	updateHoverTooltip = () => {
		this.closeHoverTooltip();
		const hovered = this.state.hoveredLeg;
		if (!hovered || !this.state.map || !this.state.maps) {
			return;
		}

		const route = this.getVisibleRoutes().find((r) => r.fePkey.toString() === hovered.fePkey.toString());
		if (!route) {
			return;
		}
		const leg = route.legs.find((l) => l.index === hovered.legIndex);
		if (!leg) {
			return;
		}

		let bodyLines;
		if (leg.isDummy) {
			bodyLines = `<div style="font-weight:600;font-size:12px;color:#000;">Home</div>`;
		} else {
			bodyLines = [
				`<div style="font-weight:600;font-size:12px;color:#000;">${leg.fromLabel} → ${leg.toLabel}</div>`,
				`<div style="font-weight:600;font-size:12px;color:#000;">${leg.drive_time} min${leg.miles ? ` · ${leg.miles} mi` : ''}</div>`,
			].join('');
		}

		const html = [
			`<div style="padding:8px 12px;background:#fff;border:2px solid ${route.color};border-radius:4px;`,
			`box-shadow:0 2px 6px rgba(0,0,0,0.2);white-space:nowrap;line-height:1.4;">`,
			`<div style="font-weight:700;font-size:14px;color:${route.color};margin-bottom:2px;">${route.name}</div>`,
			bodyLines,
			`</div>`,
		].join('');

		if (!this.hoverTooltipOverlayClass) {
			this.hoverTooltipOverlayClass = buildHoverTooltipOverlayClass(this.state.maps);
		}

		const position = offsetLegLabelPosition(leg.path[0], leg.path[1], 120);
		this.hoverTooltipOverlay = new this.hoverTooltipOverlayClass(position, html);
		this.hoverTooltipOverlay.setMap(this.state.map);
	}

	getFEMaps = async (allowRecenter = false) => {
		if (this.state.fetching === true) {
			return;
		} else {
			this.state.fetching = true
		}
		let model = new API.BuildClass(Types.Users._name, API.RequestType.READ);
		model.filter = {
			'filterRegion': this.state.filterRegion ?? this.props.filterRegion,
			'filterDate': this.props.filterDate ?? moment().format(),
			'popular': true
		}
		let maps = await model.submit(null, 'GET_FIELD_ENGINEERS_MAPS');

		const recenter = allowRecenter || this.shouldRecenterMap();

		let users = [];
		let routes = [];
		let keyedColors = [];

		await Promise.all(await maps.items.map(async (user, i) => {

			if (user.latitude === null || user.longitude === null) {
				user.newLatLong = await resolveLatLng(user.address);

				if (user.newLatLong?.latitude != null && user.newLatLong?.longitude != null) {
					let usersModel = new API.BuildClass(Types.Users._name, API.RequestType.UPDATE);
					usersModel.object.user = user.pkey;
					usersModel.object.latitude = user.newLatLong.latitude;
					usersModel.object.longitude = user.newLatLong.longitude;
					user.latitude = user.newLatLong.latitude;
					user.longitude = user.newLatLong.longitude;
					await usersModel.submit(null, 'POST_COORDS', true);
				}
			}

			await Promise.all(user.appointments.map(async (appointment) => {
				if (appointment.LocationOverride || appointment.LocationOverrideCoords) {
					if (appointment.LocationOverrideCoords) {
						const overrideCoords = parseLatLngString(appointment.LocationOverrideCoords);
						if (overrideCoords) {
							appointment.LocationOverride = overrideCoords;
						}
					} else {
						const resolved = await resolveLatLng(appointment.LocationOverride);
						if (resolved?.latitude == null || resolved?.longitude == null) {
							return true;
						}
						appointment.LocationOverride = resolved;
						const coords = `${resolved.latitude}, ${resolved.longitude}`;
						if (Object.keys(appointment).includes('Informational')) {
							let ticketModel = new API.BuildClass(Types.Tickets._name, API.RequestType.UPDATE);
							ticketModel.object.pkey = appointment.Pkey
							ticketModel.object.location_override_coords = coords;
							await ticketModel.submit(null, 'POST_LOCATION_OVERRIDE', true);
						} else {
							let calendarModel = new API.BuildClass(Types.Calendar._name, API.RequestType.UPDATE);
							calendarModel.object.pkey = appointment.Pkey
							calendarModel.object.location_override_coords = coords;
							await calendarModel.submit(null, 'POST_LOCATION_OVERRIDE', true);
						}

					}
				}
				return true;
			}))

			// Home + tiny offset keeps house icon oriented at 0deg; do not label that dummy leg.
			let userCoords = user.latitude ? [{
				lat: parseFloat(user.latitude),
				lng: parseFloat(user.longitude)
			}, {lat: parseFloat(user.latitude) + .0000001, lng: parseFloat(user.longitude)}] : [];

			const feName = user.name_first + " " + user.name_last;
			users.push({value: user.pkey, label: feName})

			user.appointments.forEach((appt) => {
					userCoords.push({
						lat: appt.LocationOverride ? parseFloat(appt.LocationOverride.latitude) : parseFloat(appt.CustomersLocations.Latitude),
						lng: appt.LocationOverride ? parseFloat(appt.LocationOverride.longitude) : parseFloat(appt.CustomersLocations.Longitude)
					})
				}
			)

			if (userCoords.length > 0) {
				const color = USER_COLORS[i % USER_COLORS.length];

				if (!keyedColors[user.pkey]) {
					keyedColors[user.pkey] = color;
				}

				const legs = [];
				for (let index = 1; index < userCoords.length; index++) {
					const path = [
						{lat: userCoords[index - 1].lat, lng: userCoords[index - 1].lng},
						{lat: userCoords[index].lat, lng: userCoords[index].lng}
					];
					const isDummy = index === 1;
					const timing = isDummy ? {miles: 0, drive_time: 0} : driveTimeForLeg(path[0], path[1]);
					const fromLabel = index <= 2 ? 'Home' : `Stop ${index - 2}`;
					const toLabel = isDummy ? 'Home' : `Stop ${index - 1}`;
					legs.push({
						index: index - 1,
						path,
						isDummy,
						miles: timing.miles,
						drive_time: timing.drive_time,
						midpoint: legMidpoint(path[0], path[1]),
						stopNumber: isDummy ? null : (index - 1),
						stopLat: path[1].lat,
						stopLng: path[1].lng,
						fromLabel,
						toLabel,
					});
				}

				routes.push({
					fePkey: user.pkey,
					name: feName,
					color,
					legs,
					lastLat: userCoords[userCoords.length - 1].lat,
					lastLng: userCoords[userCoords.length - 1].lng,
				});
			}
			return true;
		}))

		let selectedRegion = this.state.regions.filter(region => {
			return region.pkey == (this.state.filterRegion ?? this.props.filterRegion)
		})[0];

		const nextState = {
			routes,
			users,
			hoveredLeg: null,
			filterRegion: this.state.filterRegion ?? this.props.filterRegion,
			selectedRegion: selectedRegion ? {value: selectedRegion.pkey, label: selectedRegion.name} : null,
		};

		await this.setState(nextState);

		if (recenter) {
			this.panToRegion(maps.regionCoords);
		}
		this.updateCustomerMarker();
		if (this.props.selectedAddress) {
			this.panToCustomer();
		}
		if (typeof (this.props.setColors) === 'function') {
			this.props.setColors(keyedColors);
		}
		this.state.fetching = false;
	}

	updateRegion = (region, name) => {
		this.setState({region, name})
	}

	getRegions = async () => {
		let model = new API.BuildClass(Types.Regions._name, API.RequestType.READ_ALL);
		this.read = 'GET_ALL';
		model.limit = 5000;
		if (this.props.popularRegions) {
			model.filter = {
				'filterPopular': true
			}
		}
		try {
			this.regions = await model.submit(null, this.read);
			this.regions = Object.keys(this.regions).map((key) => this.regions[key]);
			this.regions = this.regions[3];
			this.regions = this.regions.sort(function (a, b) {
				return a.name.localeCompare(b.name)
			})
			this.regions = this.regions.filter(region => region.latitude > 0 || region.latitude < 0)
			this.setState({
				regions: this.regions
			});
		} catch (error) {
		}
		return this.state.regions;
	}

	handleUsers = async (value) => {
		let users = [];
		if (value) {
			for (let i = 0; i < value.length; i++) {
				users.splice(0, 0, value[i]);
			}
			this.setState({selectedUsers: users})
		} else {
			this.setState({selectedUsers: []});
		}
	}

	renderMap = (height) => {
		let zoom = !this.state.filterRegion && !this.props.filterRegion ? 10 : [27, 28, 5, 6, 19].includes(this.state.filterRegion ?? this.props.filterRegion) ? 9 : [31, 12, 25].includes(this.state.filterRegion ?? this.props.filterRegion) ? 11 : 10;
		let object = this.state;
		const mapCenter = object.center || object.defaultCenter;

		return (
			<div style={{height, width: '100%'}}>
				<GoogleMapReact
					bootstrapURLKeys={getGoogleMapBootstrapURLKeys()}
					defaultCenter={mapCenter}
					defaultZoom={zoom}
					zoom={zoom}
					yesIWantToUseGoogleMapApiInternals
					onGoogleApiLoaded={({map, maps}) => this.apiIsLoaded(map, maps)}
					ref={this.mapRef}
				>
					<Framework.Elements.LoadingSpinner isLoading={object.loading}/>
				</GoogleMapReact>
			</div>
		);
	}

	render() {
		if (this.props.onlyMap) {
			return this.renderMap(this.props.noSubmit ? '70vh' : '50vh');
		}

		return (
			<div>
				<div className={'row'}>
					<div className={'col-12'}>
						<div className={'section-title'}>
							<h4 className="section-title">FE Routes For {moment(this.props.filterDate).format('dddd MMM D') ?? moment().format('dddd MMM D')}</h4>
								<div className={'flex-end text-right'}>
									<Link target={'_blank'}>
										<Button size={'md'} color={'primary'}>Duplicate Page</Button>
									</Link>
										<Button color="info" onClick={async () => {
											this.setState({loading: true})
											await this.getFEMaps(false).then(() => {
												this.setState({loading: false})
											})}}>
											Refresh
										</Button>
								</div>
						</div>
					</div>
					<div className={'col-12'}>
						<div className={'row'} style={{alignItems: 'center'}}>
							<div className={'col-5'}>
								<Parts.RegionSelectToggle
									parent={this}
									handleRegionsCallback={async () => {await this.getFEMaps(true);}}
									multi={false}
									noCycle={this.props.noCycle}
								/>
							</div>
							<div className={"col-5"}>
								<Select
									key={this.state.users}
									placeholder={"User Select"}
									isMulti
									isClearable
									value={this.state.selectedUsers}
									onChange={this.handleUsers}
									options={this.state.users}
								/>
							</div>
							<div className={'col-2'} style={{paddingTop: 6}}>
								<CustomInput
									type="switch"
									id="dispatch-show-drive-times"
									label="Drive times"
									checked={this.state.showDriveTimes}
									onChange={(e) => this.setState({showDriveTimes: e.target.checked})}
								/>
							</div>
						</div>
					</div>
					<div className={'col-12'}>
						{this.renderMap(this.props.noSubmit ? '70vh' : '50vh')}
					</div>
				</div>
			</div>
		)
	}
}
